import * as React from 'react';
import { type VariantProps } from 'tailwind-variants';

import { type ExtendedComponentProps } from '../types';
import { cn, tv } from '../utils';

/* -------------------------------------------------------------------------------------------------
 * Alert
 * -----------------------------------------------------------------------------------------------*/

const alertVariants = tv({
	base: 'relative w-full border',
	variants: {
		variant: {
			filled: '',
			subtle: '',
			outlined: '',
			unstyled: '',
		},
		color: {
			primary: '',
			zinc: '',
			red: '',
			orange: '',
			amber: '',
			yellow: '',
			lime: '',
			green: '',
			emerald: '',
			teal: '',
			cyan: '',
			sky: '',
			blue: '',
			indigo: '',
			violet: '',
			purple: '',
			fuchsia: '',
			pink: '',
			rose: '',
			unstyled: '',
		},
		size: {
			default: 'text-sm px-4 py-3 rounded-lg [&:has(svg)>.nexusUiAlertDescription]:pl-6',
			unstyled: '',
		},
	},
	compoundVariants: [
		/** primary */
		{
			variant: 'filled',
			color: 'primary',
			className: cn('bg-lucky-point-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-lucky-point-950'),
		},
		{
			variant: 'subtle',
			color: 'primary',
			className: cn(
				'bg-lucky-point-25 border-transparent text-lucky-point-900 [&>svg]:text-lucky-point-900',
				'dark:bg-lucky-point-950/50 dark:text-lucky-point-200 dark:[&>svg]:text-lucky-point-200',
			),
		},
		{
			variant: 'outlined',
			color: 'primary',
			className: cn(
				'bg-white border-lucky-point-500 text-lucky-point-700 [&>svg]:text-lucky-point-700',
				'dark:bg-transparent dark:border-lucky-point-700 dark:text-lucky-point-300 dark:[&>svg]:text-lucky-point-300',
			),
		},
		/** zinc */
		{
			variant: 'filled',
			color: 'zinc',
			className: cn('bg-zinc-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-zinc-700'),
		},
		{
			variant: 'subtle',
			color: 'zinc',
			className: cn(
				'bg-zinc-100 border-transparent text-zinc-700 [&>svg]:text-zinc-700',
				'dark:bg-zinc-800 dark:text-zinc-200 dark:[&>svg]:text-zinc-200',
			),
		},
		{
			variant: 'outlined',
			color: 'zinc',
			className: cn(
				'bg-white border-zinc-200 text-zinc-900 [&>svg]:text-zinc-900',
				'dark:bg-transparent dark:border-zinc-700 dark:text-zinc-300 dark:[&>svg]:text-zinc-300',
			),
		},
		/** red */
		{
			variant: 'filled',
			color: 'red',
			className: cn('bg-red-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-red-900'),
		},
		{
			variant: 'subtle',
			color: 'red',
			className: cn(
				'bg-red-100 border-transparent text-red-900 [&>svg]:text-red-900',
				'dark:bg-red-950/50 dark:text-red-200 dark:[&>svg]:text-red-200',
			),
		},
		{
			variant: 'outlined',
			color: 'red',
			className: cn(
				'bg-white border-red-500 text-red-700 [&>svg]:text-red-700',
				'dark:bg-transparent dark:border-red-700 dark:text-red-300 dark:[&>svg]:text-red-300',
			),
		},
		/** orange */
		{
			variant: 'filled',
			color: 'orange',
			className: cn('bg-orange-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-orange-900'),
		},
		{
			variant: 'subtle',
			color: 'orange',
			className: cn(
				'bg-orange-100 border-transparent text-orange-700 [&>svg]:text-orange-700',
				'dark:bg-orange-950/50 dark:text-orange-200 dark:[&>svg]:text-orange-200',
			),
		},
		{
			variant: 'outlined',
			color: 'orange',
			className: cn(
				'bg-white border-orange-500 text-orange-700 [&>svg]:text-orange-700',
				'dark:bg-transparent dark:border-orange-700 dark:text-orange-300 dark:[&>svg]:text-orange-300',
			),
		},
		/** amber */
		{
			variant: 'filled',
			color: 'amber',
			className: cn('bg-amber-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-amber-900'),
		},
		{
			variant: 'subtle',
			color: 'amber',
			className: cn(
				'bg-amber-100 border-transparent text-amber-700 [&>svg]:text-amber-700',
				'dark:bg-amber-950/50 dark:text-amber-200 dark:[&>svg]:text-amber-200',
			),
		},
		{
			variant: 'outlined',
			color: 'amber',
			className: cn(
				'bg-white border-amber-50 text-amber-900 [&>svg]:text-amber-900',
				'dark:bg-transparent dark:border-amber-700 dark:text-amber-300 dark:[&>svg]:text-amber-300',
			),
		},
		/** yellow */
		{
			variant: 'filled',
			color: 'yellow',
			className: cn('bg-yellow-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-yellow-900'),
		},
		{
			variant: 'subtle',
			color: 'yellow',
			className: cn(
				'bg-yellow-100 border-transparent text-yellow-700 [&>svg]:text-yellow-700',
				'dark:bg-yellow-950/50 dark:text-yellow-200 dark:[&>svg]:text-yellow-200',
			),
		},
		{
			variant: 'outlined',
			color: 'yellow',
			className: cn(
				'bg-white border-yellow-50 text-yellow-900 [&>svg]:text-yellow-900',
				'dark:bg-transparent dark:border-yellow-700 dark:text-yellow-300 dark:[&>svg]:text-yellow-300',
			),
		},
		/** lime */
		{
			variant: 'filled',
			color: 'lime',
			className: cn('bg-lime-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-lime-900'),
		},
		{
			variant: 'subtle',
			color: 'lime',
			className: cn(
				'bg-lime-100 border-transparent text-lime-700 [&>svg]:text-lime-700',
				'dark:bg-lime-950/50 dark:text-lime-200 dark:[&>svg]:text-lime-200',
			),
		},
		{
			variant: 'outlined',
			color: 'lime',
			className: cn(
				'bg-white border-lime-50 text-lime-900 [&>svg]:text-lime-900',
				'dark:bg-transparent dark:border-lime-700 dark:text-lime-300 dark:[&>svg]:text-lime-300',
			),
		},
		/** green */
		{
			variant: 'filled',
			color: 'green',
			className: cn('bg-green-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-green-900'),
		},
		{
			variant: 'subtle',
			color: 'green',
			className: cn(
				'bg-green-100 border-transparent text-green-700 [&>svg]:text-green-700',
				'dark:bg-green-950/50 dark:text-green-200 dark:[&>svg]:text-green-200',
			),
		},
		{
			variant: 'outlined',
			color: 'green',
			className: cn(
				'bg-white border-green-50 text-green-900 [&>svg]:text-green-900',
				'dark:bg-transparent dark:border-green-700 dark:text-green-300 dark:[&>svg]:text-green-300',
			),
		},
		/** emerald */
		{
			variant: 'filled',
			color: 'emerald',
			className: cn('bg-emerald-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-emerald-900'),
		},
		{
			variant: 'subtle',
			color: 'emerald',
			className: cn(
				'bg-emerald-100 border-transparent text-emerald-700 [&>svg]:text-emerald-700',
				'dark:bg-emerald-950/50 dark:text-emerald-200 dark:[&>svg]:text-emerald-200',
			),
		},
		{
			variant: 'outlined',
			color: 'emerald',
			className: cn(
				'bg-white border-emerald-50 text-emerald-900 [&>svg]:text-emerald-900',
				'dark:bg-transparent dark:border-emerald-700 dark:text-emerald-300 dark:[&>svg]:text-emerald-300',
			),
		},
		/** teal */
		{
			variant: 'filled',
			color: 'teal',
			className: cn('bg-teal-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-teal-900'),
		},
		{
			variant: 'subtle',
			color: 'teal',
			className: cn(
				'bg-teal-100 border-transparent text-teal-700 [&>svg]:text-teal-700',
				'dark:bg-teal-950/50 dark:text-teal-200 dark:[&>svg]:text-teal-200',
			),
		},
		{
			variant: 'outlined',
			color: 'teal',
			className: cn(
				'bg-white border-teal-50 text-teal-900 [&>svg]:text-teal-900',
				'dark:bg-transparent dark:border-teal-700 dark:text-teal-300 dark:[&>svg]:text-teal-300',
			),
		},
		/** cyan */
		{
			variant: 'filled',
			color: 'cyan',
			className: cn('bg-cyan-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-cyan-900'),
		},
		{
			variant: 'subtle',
			color: 'cyan',
			className: cn(
				'bg-cyan-100 border-transparent text-cyan-700 [&>svg]:text-cyan-700',
				'dark:bg-cyan-950/50 dark:text-cyan-200 dark:[&>svg]:text-cyan-200',
			),
		},
		{
			variant: 'outlined',
			color: 'cyan',
			className: cn(
				'bg-white border-cyan-50 text-cyan-900 [&>svg]:text-cyan-900',
				'dark:bg-transparent dark:border-cyan-700 dark:text-cyan-300 dark:[&>svg]:text-cyan-300',
			),
		},
		/** sky */
		{
			variant: 'filled',
			color: 'sky',
			className: cn('bg-sky-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-sky-900'),
		},
		{
			variant: 'subtle',
			color: 'sky',
			className: cn(
				'bg-sky-100 border-transparent text-sky-900 [&>svg]:text-sky-900',
				'dark:bg-sky-950/50 dark:text-sky-200 dark:[&>svg]:text-sky-200',
			),
		},
		{
			variant: 'outlined',
			color: 'sky',
			className: cn(
				'bg-white border-sky-50 text-sky-900 [&>svg]:text-sky-900',
				'dark:bg-transparent dark:border-sky-700 dark:text-sky-300 dark:[&>svg]:text-sky-300',
			),
		},
		/** blue */
		{
			variant: 'filled',
			color: 'blue',
			className: cn('bg-blue-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-blue-900'),
		},
		{
			variant: 'subtle',
			color: 'blue',
			className: cn(
				'bg-blue-100 border-transparent text-blue-700 [&>svg]:text-blue-700',
				'dark:bg-blue-950/50 dark:text-blue-200 dark:[&>svg]:text-blue-200',
			),
		},
		{
			variant: 'outlined',
			color: 'blue',
			className: cn(
				'bg-white border-blue-50 text-blue-900 [&>svg]:text-blue-900',
				'dark:bg-transparent dark:border-blue-700 dark:text-blue-300 dark:[&>svg]:text-blue-300',
			),
		},
		/** indigo */
		{
			variant: 'filled',
			color: 'indigo',
			className: cn('bg-indigo-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-indigo-900'),
		},
		{
			variant: 'subtle',
			color: 'indigo',
			className: cn(
				'bg-indigo-100 border-transparent text-indigo-700 [&>svg]:text-indigo-700',
				'dark:bg-indigo-950/50 dark:text-indigo-200 dark:[&>svg]:text-indigo-200',
			),
		},
		{
			variant: 'outlined',
			color: 'indigo',
			className: cn(
				'bg-white border-indigo-50 text-indigo-900 [&>svg]:text-indigo-900',
				'dark:bg-transparent dark:border-indigo-700 dark:text-indigo-300 dark:[&>svg]:text-indigo-300',
			),
		},
		/** violet */
		{
			variant: 'filled',
			color: 'violet',
			className: cn('bg-violet-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-violet-900'),
		},
		{
			variant: 'subtle',
			color: 'violet',
			className: cn(
				'bg-violet-100 border-transparent text-violet-700 [&>svg]:text-violet-700',
				'dark:bg-violet-950/50 dark:text-violet-200 dark:[&>svg]:text-violet-200',
			),
		},
		{
			variant: 'outlined',
			color: 'violet',
			className: cn(
				'bg-white border-violet-50 text-violet-900 [&>svg]:text-violet-900',
				'dark:bg-transparent dark:border-violet-700 dark:text-violet-300 dark:[&>svg]:text-violet-300',
			),
		},
		/** purple */
		{
			variant: 'filled',
			color: 'purple',
			className: cn('bg-purple-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-purple-900'),
		},
		{
			variant: 'subtle',
			color: 'purple',
			className: cn(
				'bg-purple-100 border-transparent text-purple-700 [&>svg]:text-purple-700',
				'dark:bg-purple-950/50 dark:text-purple-200 dark:[&>svg]:text-purple-200',
			),
		},
		{
			variant: 'outlined',
			color: 'purple',
			className: cn(
				'bg-white border-purple-50 text-purple-900 [&>svg]:text-purple-900',
				'dark:bg-transparent dark:border-purple-700 dark:text-purple-300 dark:[&>svg]:text-purple-300',
			),
		},
		/** fuchsia */
		{
			variant: 'filled',
			color: 'fuchsia',
			className: cn('bg-fuchsia-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-fuchsia-900'),
		},
		{
			variant: 'subtle',
			color: 'fuchsia',
			className: cn(
				'bg-fuchsia-100 border-transparent text-fuchsia-700 [&>svg]:text-fuchsia-700',
				'dark:bg-fuchsia-950/50 dark:text-fuchsia-200 dark:[&>svg]:text-fuchsia-200',
			),
		},
		{
			variant: 'outlined',
			color: 'fuchsia',
			className: cn(
				'bg-white border-fuchsia-50 text-fuchsia-900 [&>svg]:text-fuchsia-900',
				'dark:bg-transparent dark:border-fuchsia-700 dark:text-fuchsia-300 dark:[&>svg]:text-fuchsia-300',
			),
		},
		/** pink */
		{
			variant: 'filled',
			color: 'pink',
			className: cn('bg-pink-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-pink-900'),
		},
		{
			variant: 'subtle',
			color: 'pink',
			className: cn(
				'bg-pink-100 border-transparent text-pink-700 [&>svg]:text-pink-700',
				'dark:bg-pink-950/50 dark:text-pink-200 dark:[&>svg]:text-pink-200',
			),
		},
		{
			variant: 'outlined',
			color: 'pink',
			className: cn(
				'bg-white border-pink-50 text-pink-900 [&>svg]:text-pink-900',
				'dark:bg-transparent dark:border-pink-700 dark:text-pink-300 dark:[&>svg]:text-pink-300',
			),
		},
		/** rose */
		{
			variant: 'filled',
			color: 'rose',
			className: cn('bg-rose-500 border-transparent text-white [&>svg]:text-white', 'dark:bg-rose-900'),
		},
		{
			variant: 'subtle',
			color: 'rose',
			className: cn(
				'bg-rose-100 border-transparent text-rose-700 [&>svg]:text-rose-700',
				'dark:bg-rose-950/50 dark:text-rose-200 dark:[&>svg]:text-rose-200',
			),
		},
		{
			variant: 'outlined',
			color: 'rose',
			className: cn(
				'bg-white border-rose-50 text-rose-900 [&>svg]:text-rose-900',
				'dark:bg-transparent dark:border-rose-700 dark:text-rose-300 dark:[&>svg]:text-rose-300',
			),
		},
	],
	defaultVariants: {
		size: 'default',
		variant: 'subtle',
		color: 'primary',
	},
});

interface UiAlertProps extends ExtendedComponentProps<VariantProps<typeof alertVariants>, 'div'> {}

const UiAlert = React.forwardRef<HTMLDivElement, UiAlertProps>(({ className, variant, color, size, ...props }, ref) => (
	<div
		ref={ref}
		role="alert"
		className={cn(
			alertVariants({
				variant,
				color,
				size,
			}),
			className,
		)}
		{...props}
	/>
));
UiAlert.displayName = 'UiAlert';

/* -------------------------------------------------------------------------------------------------
 * Alert / Header
 * -----------------------------------------------------------------------------------------------*/

interface UiAlertHeaderProps extends ExtendedComponentProps<{}, 'div'> {}

const UiAlertHeader = React.forwardRef<HTMLParagraphElement, UiAlertHeaderProps>(({ className, ...props }, ref) => (
	<div ref={ref} className={cn('flex items-center mb-1 gap-2 [&>svg]:size-4', className)} {...props} />
));
UiAlertHeader.displayName = 'UiAlertHeader';

/* -------------------------------------------------------------------------------------------------
 * Alert / Title
 * -----------------------------------------------------------------------------------------------*/

interface UiAlertTitleProps extends ExtendedComponentProps<{}, 'h5'> {}

const UiAlertTitle = React.forwardRef<HTMLParagraphElement, UiAlertTitleProps>(({ className, ...props }, ref) => (
	<h5 ref={ref} className={cn('font-medium leading-none tracking-tight w-full', className)} {...props} />
));
UiAlertTitle.displayName = 'UiAlertTitle';

/* -------------------------------------------------------------------------------------------------
 * Alert / Description
 * -----------------------------------------------------------------------------------------------*/

interface UiAlertDescriptionProps extends ExtendedComponentProps<{}, 'p'> {}

const UiAlertDescription = React.forwardRef<HTMLParagraphElement, UiAlertDescriptionProps>(({ className, ...props }, ref) => (
	<div ref={ref} className={cn('nexusUiAlertDescription text-sm [&_p]:leading-relaxed opacity-75', 'dark:opacity-90', className)} {...props} />
));
UiAlertDescription.displayName = 'UiAlertDescription';

/* -----------------------------------------------------------------------------------------------*/

export { UiAlert, UiAlertTitle, UiAlertDescription, UiAlertHeader, UiAlertProps };
